import { memo, useState } from "react";
import { Flex } from "../../flex";
import { RedoTooltipProps, Tooltip } from "./tooltip";

interface OptionalTooltipProps {
  meetsCondition: boolean;
  children: React.ReactElement;
  tooltipProps: Omit<RedoTooltipProps, "children">;
  wrapperClass?: string;
  wrapperStyle?: React.CSSProperties;
  hideOnHover?: boolean;
}

// Stateful component that's only used when hideOnHover is true
const HoverControlledTooltip = memo(function HoverControlledTooltip({
  tooltipProps,
  wrapperClass,
  wrapperStyle,
  children,
}: Omit<
  OptionalTooltipProps,
  "meetsCondition" | "hideOnHover"
>): React.ReactElement {
  const [tooltipOpen, setTooltipOpen] = useState(false);

  return (
    <Tooltip {...tooltipProps} open={tooltipOpen}>
      <Flex
        as="span"
        className={wrapperClass}
        onMouseEnter={() => setTooltipOpen(true)}
        onMouseLeave={() => setTooltipOpen(false)}
        style={wrapperStyle}
      >
        {children}
      </Flex>
    </Tooltip>
  );
});

export const OptionalTooltip = memo(function OptionalTooltip({
  meetsCondition,
  children,
  tooltipProps,
  wrapperClass,
  wrapperStyle,
  hideOnHover = false,
}: OptionalTooltipProps): React.ReactElement {
  if (!meetsCondition) {
    return (
      <Flex as="span" className={wrapperClass} style={wrapperStyle}>
        {children}
      </Flex>
    );
  }

  if (hideOnHover) {
    return (
      <HoverControlledTooltip
        tooltipProps={tooltipProps}
        wrapperClass={wrapperClass}
        wrapperStyle={wrapperStyle}
      >
        {children}
      </HoverControlledTooltip>
    );
  }

  return (
    <Tooltip {...tooltipProps}>
      <Flex as="span" className={wrapperClass} style={wrapperStyle}>
        {children}
      </Flex>
    </Tooltip>
  );
});
